import type { AtlasEdge, AtlasNode, AtlasWorkspace, Confidence, NodeType, RelationshipType } from './types';
import { migrateWorkspace } from './workspace-migration';

const NODE_TYPES = new Set<NodeType>([
  'capability', 'application', 'service', 'data_store', 'identity',
  'infrastructure', 'vendor', 'procedure', 'role', 'control',
]);
const RELATIONSHIP_TYPES = new Set<RelationshipType>([
  'DEPENDS_ON', 'AUTHENTICATES_VIA', 'TRUSTS_IDENTITY_FROM', 'STORES_DATA_IN',
  'HOSTED_ON', 'PROVIDED_BY', 'FALLS_BACK_TO', 'REQUIRES_ROLE', 'HAS_ACCESS_TO',
  'SENDS_DATA_TO', 'CONNECTS_TO', 'MONITORED_BY',
]);
const CONFIDENCE = new Set<Confidence>(['confirmed', 'reported', 'inferred', 'unknown']);
const ENVIRONMENTS = new Set(['production', 'staging', 'development', 'other']);
const STRENGTHS = new Set(['hard', 'degraded', 'optional']);
const SCENARIO_TYPES = new Set(['outage', 'breach']);
const IMPACT_SEVERITIES = new Set(['critical', 'high', 'moderate', 'low']);
const FINDING_CATEGORIES = new Set(['graph', 'control', 'policy', 'procedure', 'ownership', 'recovery']);
const FINDING_PRIORITIES = new Set(['critical', 'high', 'medium', 'low']);
const FINDING_STATUSES = new Set(['open', 'in_progress', 'closed']);
const RECOVERY_CONFIDENCE = new Set(['validated', 'demonstrated', 'documented', 'assumed', 'unknown']);
const FAILURE_EFFECTS = new Set(['unavailable', 'degraded', 'unsafe', 'integrity_risk', 'none']);

export interface WorkspaceValidationResult {
  workspace: AtlasWorkspace | null;
  errors: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function validNode(value: unknown, index: number, errors: string[]): value is AtlasNode {
  if (!isRecord(value)) {
    errors.push(`Entity ${index + 1} is not an object.`);
    return false;
  }
  const prefix = `Entity ${index + 1}`;
  if (typeof value.id !== 'string' || !value.id.trim()) errors.push(`${prefix} needs an ID.`);
  if (typeof value.name !== 'string' || !value.name.trim()) errors.push(`${prefix} needs a name.`);
  if (typeof value.description !== 'string' || typeof value.owner !== 'string') errors.push(`${prefix} has invalid descriptive fields.`);
  if (!NODE_TYPES.has(value.type as NodeType)) errors.push(`${prefix} has an unsupported type.`);
  if (!Number.isInteger(value.criticality) || Number(value.criticality) < 1 || Number(value.criticality) > 5) errors.push(`${prefix} has invalid criticality.`);
  if (!CONFIDENCE.has(value.confidence as Confidence)) errors.push(`${prefix} has invalid confidence.`);
  if (!ENVIRONMENTS.has(String(value.environment))) errors.push(`${prefix} has an invalid environment.`);
  if (!Number.isFinite(value.x) || !Number.isFinite(value.y)) errors.push(`${prefix} needs finite graph coordinates.`);
  if (value.rtoMinutes !== undefined && (!Number.isFinite(value.rtoMinutes) || Number(value.rtoMinutes) < 0)) errors.push(`${prefix} has an invalid RTO.`);
  for (const [field, label] of [['impactToleranceMinutes', 'impact tolerance'], ['estimatedRecoveryMinutes', 'estimated recovery'], ['observedRecoveryMinutes', 'observed recovery']] as const) {
    if (value[field] !== undefined && (!Number.isFinite(value[field]) || Number(value[field]) < 0)) errors.push(`${prefix} has invalid ${label}.`);
  }
  if (value.recoveryConfidence !== undefined && !RECOVERY_CONFIDENCE.has(String(value.recoveryConfidence))) errors.push(`${prefix} has invalid recovery confidence.`);
  if (!isStringArray(value.locationIds)) errors.push(`${prefix} has invalid locations.`);
  if (!isStringArray(value.tags) || !isStringArray(value.dataClassifications) || !isStringArray(value.sourceRefs)) errors.push(`${prefix} has invalid list fields.`);
  return errors.length === 0;
}

function validEdge(value: unknown, index: number, nodeIds: Set<string>, errors: string[]): value is AtlasEdge {
  if (!isRecord(value)) {
    errors.push(`Relationship ${index + 1} is not an object.`);
    return false;
  }
  const prefix = `Relationship ${index + 1}`;
  if (typeof value.id !== 'string' || !value.id.trim()) errors.push(`${prefix} needs an ID.`);
  if (!nodeIds.has(String(value.sourceId))) errors.push(`${prefix} references a missing source entity.`);
  if (!nodeIds.has(String(value.targetId))) errors.push(`${prefix} references a missing target entity.`);
  if (value.sourceId === value.targetId) errors.push(`${prefix} cannot connect an entity to itself.`);
  if (!RELATIONSHIP_TYPES.has(value.type as RelationshipType)) errors.push(`${prefix} has an unsupported type.`);
  if (!STRENGTHS.has(String(value.strength))) errors.push(`${prefix} has invalid strength.`);
  if (!CONFIDENCE.has(value.confidence as Confidence)) errors.push(`${prefix} has invalid confidence.`);
  if (!Number.isFinite(value.toleranceMinutes) || Number(value.toleranceMinutes) < 0) errors.push(`${prefix} has invalid tolerance.`);
  if (!FAILURE_EFFECTS.has(String(value.failureEffect))) errors.push(`${prefix} has an invalid failure effect.`);
  if (!Number.isFinite(value.propagationDelayMinutes) || Number(value.propagationDelayMinutes) < 0) errors.push(`${prefix} has an invalid propagation delay.`);
  if (value.minimumServiceLevelPercent !== undefined && (!Number.isFinite(value.minimumServiceLevelPercent) || Number(value.minimumServiceLevelPercent) < 0 || Number(value.minimumServiceLevelPercent) > 100)) errors.push(`${prefix} has an invalid minimum service level.`);
  if (!isStringArray(value.evidenceIds)) errors.push(`${prefix} has invalid evidence references.`);
  return errors.length === 0;
}

export function validateWorkspace(input: unknown): WorkspaceValidationResult {
  const errors: string[] = [];
  const value = migrateWorkspace(input);
  if (!isRecord(value) || value.format !== 'inventivehq.dependency-atlas' || value.version !== 2) {
    return { workspace: null, errors: ['This is not a supported Dependency Atlas workspace.'] };
  }
  if (typeof value.id !== 'string' || !value.id.trim()) errors.push('Workspace ID is missing.');
  if (typeof value.name !== 'string' || !value.name.trim()) errors.push('Workspace name is missing.');
  if (typeof value.updatedAt !== 'string') errors.push('Workspace update timestamp is missing.');
  if (!Array.isArray(value.nodes) || !Array.isArray(value.edges)) {
    return { workspace: null, errors: [...errors, 'Workspace entities and relationships must be arrays.'] };
  }

  value.nodes.forEach((node, index) => {
    const nodeErrors: string[] = [];
    validNode(node, index, nodeErrors);
    errors.push(...nodeErrors);
  });
  const nodeIds = new Set(value.nodes.map((node) => isRecord(node) ? String(node.id) : ''));
  const nodeTypes = new Map(value.nodes.flatMap((node) => isRecord(node) && typeof node.id === 'string' ? [[node.id, node.type]] : []));
  if (nodeIds.size !== value.nodes.length) errors.push('Entity IDs must be unique.');

  value.edges.forEach((edge, index) => {
    const edgeErrors: string[] = [];
    validEdge(edge, index, nodeIds, edgeErrors);
    if (isRecord(edge) && edge.type === 'FALLS_BACK_TO' && (nodeTypes.get(String(edge.sourceId)) !== 'capability' || nodeTypes.get(String(edge.targetId)) !== 'procedure')) edgeErrors.push(`Relationship ${index + 1} must connect a capability to a fallback procedure.`);
    errors.push(...edgeErrors);
  });
  const edgeIds = new Set(value.edges.map((edge) => isRecord(edge) ? String(edge.id) : ''));
  const fallbackPairs = new Set(value.edges.flatMap((edge) => isRecord(edge) && edge.type === 'FALLS_BACK_TO' ? [`${String(edge.sourceId)}\u0000${String(edge.targetId)}`] : []));
  if (edgeIds.size !== value.edges.length) errors.push('Relationship IDs must be unique.');
  if (!Array.isArray(value.scenarios) || !Array.isArray(value.findings) || !isRecord(value.tabletop) || !Array.isArray(value.fallbackProfiles)) {
    errors.push('Workspace exercise data is malformed.');
  } else {
    value.fallbackProfiles.forEach((profile, profileIndex) => {
      const prefix = `Fallback profile ${profileIndex + 1}`;
      if (!isRecord(profile) || typeof profile.id !== 'string' || nodeTypes.get(String(profile.capabilityId)) !== 'capability' || nodeTypes.get(String(profile.procedureId)) !== 'procedure' || !fallbackPairs.has(`${String(profile.capabilityId)}\u0000${String(profile.procedureId)}`) || !Number.isFinite(profile.activationMinutes) || Number(profile.activationMinutes) < 0 || !Number.isFinite(profile.throughputPercent) || Number(profile.throughputPercent) < 0 || Number(profile.throughputPercent) > 100 || !Number.isFinite(profile.maximumDurationMinutes) || Number(profile.maximumDurationMinutes) < 0 || !isStringArray(profile.requiredRoles) || !isStringArray(profile.requiredResources)) errors.push(`${prefix} is malformed.`);
    });
    const profileIds = new Set(value.fallbackProfiles.map((profile) => isRecord(profile) ? String(profile.id) : ''));
    const profilePairs = new Set(value.fallbackProfiles.map((profile) => isRecord(profile) ? `${String(profile.capabilityId)}\u0000${String(profile.procedureId)}` : ''));
    if (profileIds.size !== value.fallbackProfiles.length) errors.push('Fallback profile IDs must be unique.');
    if (profilePairs.size !== value.fallbackProfiles.length) errors.push('Each fallback relationship can have only one capacity profile.');
    value.scenarios.forEach((scenario, scenarioIndex) => {
      const prefix = `Scenario ${scenarioIndex + 1}`;
      if (!isRecord(scenario) || typeof scenario.id !== 'string' || typeof scenario.name !== 'string' || !SCENARIO_TYPES.has(String(scenario.type)) || !nodeIds.has(String(scenario.originNodeId)) || typeof scenario.createdAt !== 'string' || !Array.isArray(scenario.impacts)) {
        errors.push(`${prefix} is malformed.`);
        return;
      }
      scenario.impacts.forEach((impact, impactIndex) => {
        if (!isRecord(impact) || !nodeIds.has(String(impact.nodeId)) || !isStringArray(impact.edgeIds) || !isStringArray(impact.nodeIds) || !IMPACT_SEVERITIES.has(String(impact.severity)) || !Number.isFinite(impact.timeToImpact) || !CONFIDENCE.has(impact.confidence as Confidence)) errors.push(`${prefix} impact ${impactIndex + 1} is malformed.`);
      });
    });
    value.findings.forEach((finding, findingIndex) => {
      if (!isRecord(finding) || typeof finding.id !== 'string' || typeof finding.title !== 'string' || typeof finding.description !== 'string' || typeof finding.owner !== 'string' || !FINDING_CATEGORIES.has(String(finding.category)) || !FINDING_PRIORITIES.has(String(finding.priority)) || !FINDING_STATUSES.has(String(finding.status)) || (finding.linkedNodeId !== undefined && !nodeIds.has(String(finding.linkedNodeId)))) errors.push(`Finding ${findingIndex + 1} is malformed.`);
    });
    const tabletop = value.tabletop;
    if (typeof tabletop.title !== 'string' || typeof tabletop.objective !== 'string' || typeof tabletop.observations !== 'string' || typeof tabletop.currentInject !== 'string' || !isStringArray(tabletop.participants)) errors.push('Tabletop exercise data is malformed.');
  }

  return errors.length
    ? { workspace: null, errors: errors.slice(0, 12) }
    : { workspace: value as unknown as AtlasWorkspace, errors: [] };
}
